import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationsApi } from '@/api/services/notifications';
import type { NotificationDto } from '@/api/types/notifications';
import { useSignalRHub, type ConnectionStatus } from '@/realtime/useSignalRHub';
import { emitNotification } from './notificationBus';

const PAGE_SIZE = 30;

interface UpdatePayload {
  id?: string;
  Id?: string;
  isRead?: boolean;
  IsRead?: boolean;
}

// Normalise a server payload (live push camelCases, but ABP can PascalCase).
function normalize(raw: unknown): NotificationDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = (r.id ?? r.Id) as string | undefined;
  if (!id) return null;
  return {
    id,
    title: (r.title ?? r.Title ?? '') as string,
    message: (r.message ?? r.Message ?? '') as string,
    popupMessage: (r.popupMessage ?? r.PopupMessage ?? null) as string | null,
    data: (r.data ?? r.Data ?? null) as string | null,
    category: (r.category ?? r.Category ?? 0) as NotificationDto['category'],
    behavior: (r.behavior ?? r.Behavior ?? 0) as NotificationDto['behavior'],
    creationTime: (r.creationTime ?? r.CreationTime ?? new Date().toISOString()) as string,
    isRead: (r.isRead ?? r.IsRead) === true,
  };
}

export interface UseNotificationsResult {
  items: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  status: ConnectionStatus;
  loadMore: () => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  reload: () => void;
}

// React port of the legacy bell's NotificationService + NotificationUI data layer
// (notifications-client.js / notification-client-ui.js): initial list + unread count,
// live push via /signalr-notifications, optimistic mark-read / mark-all-read, infinite
// scroll paging and reconnect resync. The audio/toast/AI-summary side of the legacy UI
// is intentionally out of scope here (see report).
export function useNotifications(enabled: boolean): UseNotificationsResult {
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadedRef = useRef(0);
  const totalRef = useRef(0);
  const loadingRef = useRef(false);

  const initialLoad = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    const [listRes, unreadRes] = await Promise.allSettled([
      notificationsApi.getMessages({
        skipCount: 0,
        maxResultCount: PAGE_SIZE,
        sorting: 'CreationTime DESC',
      }),
      notificationsApi.getMyUnreadCount(),
    ]);

    if (listRes.status === 'fulfilled') {
      const list = (listRes.value.items ?? [])
        .map(normalize)
        .filter((x): x is NotificationDto => x !== null);
      setItems(list);
      loadedRef.current = list.length;
      totalRef.current = listRes.value.totalCount ?? list.length;
      setHasMore(loadedRef.current < totalRef.current);
    }

    if (unreadRes.status === 'fulfilled') {
      setUnreadCount(Number(unreadRes.value) || 0);
    } else if (listRes.status === 'fulfilled') {
      setUnreadCount(
        (listRes.value.items ?? []).filter((x) => !normalize(x)?.isRead).length,
      );
    }
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    void initialLoad();
  }, [initialLoad]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    void notificationsApi
      .getMessages({
        skipCount: loadedRef.current,
        maxResultCount: PAGE_SIZE,
        sorting: 'CreationTime DESC',
      })
      .then((res) => {
        const incoming = (res.items ?? [])
          .map(normalize)
          .filter((x): x is NotificationDto => x !== null);
        setItems((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          const merged = [...prev, ...incoming.filter((n) => !seen.has(n.id))];
          loadedRef.current = merged.length;
          totalRef.current = res.totalCount ?? totalRef.current;
          setHasMore(loadedRef.current < totalRef.current);
          return merged;
        });
      })
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
  }, [hasMore]);

  // ---- live push ----
  const emittedRef = useRef<Set<string>>(new Set());
  const onReceived = useCallback((...args: unknown[]) => {
    const n = normalize(args[0]);
    if (!n) return;
    setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
    loadedRef.current += 1;
    totalRef.current += 1;
    if (!n.isRead) setUnreadCount((c) => c + 1);
    // Fan out to the rest of the app (toast in the bell, data refetch in pages), once
    // per id so a duplicate push / server echo doesn't double-toast or double-refetch.
    if (!emittedRef.current.has(n.id)) {
      emittedRef.current.add(n.id);
      emitNotification(n);
    }
  }, []);

  const onUpdated = useCallback((...args: unknown[]) => {
    const p = args[0] as UpdatePayload | null;
    const id = p?.id ?? p?.Id;
    if (!id) return;
    const isRead = (p?.isRead ?? p?.IsRead) === true;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((n) => {
        if (n.id !== id || n.isRead === isRead) return n;
        changed = true;
        return { ...n, isRead };
      });
      if (changed) setUnreadCount((c) => Math.max(0, c + (isRead ? -1 : 1)));
      return next;
    });
  }, []);

  const onDeleted = useCallback((...args: unknown[]) => {
    const p = args[0] as UpdatePayload | null;
    const id = p?.id ?? p?.Id;
    if (!id) return;
    setItems((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.isRead) setUnreadCount((c) => Math.max(0, c - 1));
      loadedRef.current = Math.max(0, loadedRef.current - 1);
      totalRef.current = Math.max(0, totalRef.current - 1);
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  const { status } = useSignalRHub({
    url: '/signalr-notifications',
    enabled,
    on: {
      NotificationReceived: onReceived,
      ReceiveMessage: onReceived,
      NotificationUpdated: onUpdated,
      NotificationDeleted: onDeleted,
    },
    onConnected: (_conn, isReconnect) => {
      if (isReconnect) void initialLoad();
    },
  });

  const markRead = useCallback(async (id: string) => {
    // Optimistic; the server echoes NotificationUpdated which is idempotent here.
    let wasUnread = false;
    setItems((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        if (!n.isRead) wasUnread = true;
        return { ...n, isRead: true };
      }),
    );
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await notificationsApi.markRead(id);
    } catch (err) {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
      if (wasUnread) setUnreadCount((c) => c + 1);
      throw err;
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const snapshot = items;
    setItems((prev) => prev.map((n) => (n.isRead ? n : { ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await notificationsApi.markAllAsRead();
    } catch (err) {
      setItems(snapshot);
      setUnreadCount(snapshot.filter((n) => !n.isRead).length);
      throw err;
    }
  }, [items]);

  return {
    items,
    unreadCount,
    loading,
    hasMore,
    status,
    loadMore,
    markRead,
    markAllRead,
    reload: () => void initialLoad(),
  };
}
